import { Component, type ErrorInfo, type ReactNode } from 'react';
import { isRouteErrorResponse, useRouteError } from 'react-router-dom';
import { ErrorFallback, NotFound } from './error-page.js';

export interface ErrorBoundaryFallbackProps {
  error: unknown;
  /** Re-renders the children. Worth offering only when the cause was transient. */
  reset: () => void;
}

export interface ErrorBoundaryProps {
  children: ReactNode;
  /** Replaces the default screen. */
  fallback?: (props: ErrorBoundaryFallbackProps) => ReactNode;
  /** Somewhere to report to — an error tracker, a log endpoint. */
  onError?: (error: unknown, info: ErrorInfo) => void;
  /** Where the fallback's second button goes. `null` drops it. */
  homeHref?: string | null;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: unknown;
}

/**
 * The last line of defence, for the part of the tree React Router cannot see.
 *
 * Route render errors are caught by `RouteErrorBoundary` below, which is the
 * one worth wiring first. This class exists for everything *above* the
 * router — providers, the theme, i18n — where a throw would otherwise leave
 * a blank white document with the reason only in the console.
 *
 * It cannot use hooks (React exposes `componentDidCatch` to classes only) and
 * it must not assume a router above it, so its fallback links with a plain
 * anchor.
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    // Kept even with `onError` wired: a tracker that is down or not yet
    // configured must not be the reason nobody ever sees the stack.
    console.error('[ErrorBoundary]', error, info.componentStack);
    this.props.onError?.(error, info);
  }

  private readonly reset = () => {
    this.setState({ hasError: false, error: null });
  };

  override render() {
    if (!this.state.hasError) return this.props.children;

    const { fallback, homeHref } = this.props;
    if (fallback) return fallback({ error: this.state.error, reset: this.reset });

    // A boundary this high has no layout under it, so it supplies the
    // window-high column `ErrorPage` centres itself in.
    return (
      <div className="flex min-h-svh flex-col">
        <ErrorFallback error={this.state.error} homeHref={homeHref} />
      </div>
    );
  }
}

export interface RouteErrorBoundaryProps {
  /** Where the buttons go. Defaults to the app root. */
  homeHref?: string | null;
  className?: string;
}

/**
 * What to hand React Router as `errorElement`.
 *
 * Without one, a throw anywhere under a route renders the router's own
 * developer screen — the stack trace on a white page, with "Hey developer 👋"
 * underneath it — to whoever happened to be looking.
 *
 * A thrown 404 response lands on the same 404 page a missing route does, so
 * a loader that cannot find its record and a URL that never existed are one
 * screen rather than two.
 */
export function RouteErrorBoundary({ homeHref = '/', className }: RouteErrorBoundaryProps) {
  const error = useRouteError();

  // An `errorElement` replaces the whole route subtree, layout included, so
  // like the class boundary above it owns the height `ErrorPage` centres in.
  return (
    <div className="flex min-h-svh flex-col">
      {isRouteErrorResponse(error) ? (
        error.status === 404 ? (
          <NotFound homeHref={homeHref} className={className} />
        ) : (
          <ErrorFallback
            error={error.data}
            status={error.status}
            homeHref={homeHref}
            className={className}
          />
        )
      ) : (
        <ErrorFallback error={error} homeHref={homeHref} className={className} />
      )}
    </div>
  );
}
